import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../context/MemberContext';
import { Shield, UserPlus, LogIn, AlertCircle, CheckCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';

export const MemberAuth = () => {
    const { login, register } = useMember();
    const navigate = useNavigate();
    const [mode, setMode] = useState('login'); // 'login' or 'register'
    const [loading, setLoading] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    
    // Login form
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    
    // Register form
    const [callsign, setCallsign] = useState('');
    const [discordId, setDiscordId] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Welcome back, pilot!');
            navigate('/operations');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Login failed';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        setLoading(true);
        try {
            await register(callsign, email, password, discordId);
            setRegistrationSuccess(true);
            toast.success('Registration submitted!');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Registration failed';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (registrationSuccess) {
        return (
            <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center px-4" data-testid="member-auth-page">
                <Toaster position="top-right" theme="dark" />
                <div className="w-full max-w-md">
                    <div className="tactical-panel p-8 text-center">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">
                            Registration Submitted
                        </h2>
                        <p className="font-mono text-sm text-gray-400 mb-6">
                            Your application has been received. An admin will review and approve your account. 
                            You will be able to login once approved.
                        </p>
                        <button 
                            onClick={() => {
                                setRegistrationSuccess(false);
                                setMode('login');
                            }}
                            className="btn-primary py-3 px-6"
                        >
                            Return to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center px-4" data-testid="member-auth-page">
            <Toaster position="top-right" theme="dark" />
            
            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h1 className="font-heading font-black text-3xl text-white uppercase">
                        {mode === 'login' ? 'Member Portal' : 'Enlist Now'}
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-2">
                        {mode === 'login' 
                            ? 'Access restricted operations and intel' 
                            : 'Join the 126th Carrier Strike Group'
                        }
                    </p>
                </div>

                {/* Toggle */}
                <div className="flex mb-6">
                    <button
                        onClick={() => setMode('login')}
                        className={`flex-1 py-3 font-mono text-sm uppercase tracking-wider transition-colors ${
                            mode === 'login' 
                                ? 'bg-red-500 text-white' 
                                : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                        data-testid="login-tab"
                    >
                        <LogIn className="w-4 h-4 inline mr-2" />
                        Login
                    </button>
                    <button
                        onClick={() => setMode('register')}
                        className={`flex-1 py-3 font-mono text-sm uppercase tracking-wider transition-colors ${
                            mode === 'register' 
                                ? 'bg-red-500 text-white' 
                                : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                        data-testid="register-tab"
                    >
                        <UserPlus className="w-4 h-4 inline mr-2" />
                        Register
                    </button>
                </div>

                {/* Form */}
                <div className="tactical-panel p-6">
                    {mode === 'login' ? (
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="pilot@example.com"
                                    required
                                    data-testid="login-email"
                                />
                            </div>
                            <div>
                                <label className="form-label">Password</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    data-testid="login-password"
                                />
                            </div>
                            <button 
                                type="submit" 
                                className="btn-primary w-full py-3"
                                disabled={loading}
                                data-testid="login-submit"
                            >
                                {loading ? 'Authenticating...' : 'Access Portal'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleRegister} className="space-y-4">
                            <div>
                                <label className="form-label">Callsign</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={callsign}
                                    onChange={(e) => setCallsign(e.target.value)}
                                    placeholder="Your pilot callsign"
                                    required
                                    data-testid="register-callsign"
                                />
                            </div>
                            <div>
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="pilot@example.com"
                                    required
                                    data-testid="register-email"
                                />
                            </div>
                            <div>
                                <label className="form-label">Discord ID</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={discordId}
                                    onChange={(e) => setDiscordId(e.target.value)}
                                    placeholder="username#1234"
                                    required
                                    data-testid="register-discord"
                                />
                            </div>
                            <div>
                                <label className="form-label">Password</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Min 6 characters"
                                    required
                                    data-testid="register-password"
                                />
                            </div>
                            <div>
                                <label className="form-label">Confirm Password</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your password"
                                    required
                                    data-testid="register-confirm-password"
                                />
                            </div>
                            <button 
                                type="submit" 
                                className="btn-primary w-full py-3"
                                disabled={loading}
                                data-testid="register-submit"
                            >
                                {loading ? 'Submitting...' : 'Submit Enlistment'}
                            </button>
                        </form>
                    )}
                </div>

                {/* Info Note */}
                <div className="mt-6 tactical-panel p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-mono text-xs text-gray-400">
                            {mode === 'login' 
                                ? 'Your account must be approved by an admin before you can login. If you just registered, please wait for approval.'
                                : 'After registration, your application will be reviewed by fleet command. Once approved, you will gain access to classified operations and member areas.'
                            }
                        </p>
                    </div>
                </div>

                {/* Admin Link */}
                <div className="mt-4 text-center">
                    <a 
                        href="/login" 
                        className="font-mono text-xs text-gray-500 hover:text-gray-400 transition-colors"
                    >
                        Admin Access Portal
                    </a>
                </div>
            </div>
        </div>
    );
};
